import React, { Fragment, FunctionComponent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons/faTriangleExclamation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Body } from 'syf-component-library/ui/typography';
import {
  getAccountDetailsContent,
  getCreditLimitIncreaseContent,
} from '../../store/contentSlice';
import {
  setCliFormValues,
  getCliAccountId,
} from '../../store/creditLimitIncreaseSlice';
import { useAppDispatch } from '../../store/store';
import {
  setImagePath,
  formatAmount,
  showDateInFormat,
  validateInput,
} from '../../utils/helpers';
import { cliRequestFormStateInterface } from '../../interfaces/CreditLimitIncrease.interface';
import './creditLimitIncrease.css';

const CreditLimitIncreaseRequest: FunctionComponent = (props: any) => {
  const dispatch = useAppDispatch();
  const {
    displayName,
    last4Accountnumber,
    availableCredit,
    creditLimit,
    asOfDate: asOfDateValue,
  } = props;
  const creditLimitContent = useSelector(getCreditLimitIncreaseContent);
  const {
    requestHeading,
    disclosureText,
    eligibilityCriteriaText,
    annualNetIncomeInfoText,
    desiredAmountInfoText,
    annualNetIncomeZeroErrorText,
    desiredAmountZeroErrorText,
    fieldEmptyErrorText,
    desiredAmountInvalidValueErrorText,
    annualNetIncomeInvalidValueText,
    desiredLimitText,
    annualNetIncomeText,
  }: any = creditLimitContent;
  const selectedAccountId = useSelector(getCliAccountId);
  const {
    availableToSpendText,
    currentLimitText,
    asOfDate,
    usDollarSymbol,
  }: any = useSelector(getAccountDetailsContent);

  // Move these to content.json and remove the current text properties
  const inputHelpTexts = {
    annualNetIncome: annualNetIncomeInfoText,
    desiredAmount: desiredAmountInfoText,
  };
  const valueIsZeroErrorTexts = {
    annualNetIncome: annualNetIncomeZeroErrorText,
    desiredAmount: desiredAmountZeroErrorText,
  };
  const valueIsInvalidErrorTexts = {
    annualNetIncome: annualNetIncomeInvalidValueText,
    desiredAmount: desiredAmountInvalidValueErrorText,
  };
  const requiredErrorText: string = fieldEmptyErrorText;

  const [formState, setFormState] = useState<cliRequestFormStateInterface>({
    values: {
      annualNetIncome: '',
      desiredAmount: '',
    },
    errors: {
      annualNetIncome: '',
      desiredAmount: '',
    },
    touched: {
      annualNetIncome: false,
      desiredAmount: false,
    },
  });

  const inputValidationErrorTexts = {
    valueIsZeroErrorTexts,
    valueIsInvalidErrorTexts,
    requiredErrorText,
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const { name, value } = event.target;
    setFormState((prev: any) => ({
      values: {
        // preserve previous values, to change only one property
        ...prev.values,
        [name]: value,
      },
      errors: {
        // preserve previous error, to change only one property
        ...prev.errors,
        // calculate validations and return appropriate error text
        // or set it to empty if valid
        [name]: validateInput({
          name,
          value,
          errors: inputValidationErrorTexts,
          creditLimit,
        }),
      },
      touched: { ...prev.touched, [name]: true },
    }));
  };

  useEffect(() => {
    const { values, errors } = formState;
    const isValid = Object.values(errors).every((value) => value === '');
    const isComplete = Object.values(values).every((value) => value !== '');
    if (isValid && isComplete) {
      // set to redux only if both inputs valid
      dispatch(setCliFormValues(formState.values));
    } else {
      // reset both fields in redux if any one input is invalid
      dispatch(
        setCliFormValues({
          annualNetIncome: '',
          desiredAmount: '',
        }),
      );
    }
  }, [formState, dispatch]);

  const sanitizeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    //The regex replace entered non-numeric value to empty string
    target.value = target.value.replace(/[^0-9]/g, '');
  };

  const isDesiredAmountError =
    formState.errors.desiredAmount && formState.touched.desiredAmount;
  const isAnnualNetIncomeError =
    formState.errors.annualNetIncome && formState.touched.annualNetIncome;
  let desiredAmountInlineText = inputHelpTexts.desiredAmount;
  let annualIncomeInlineText = inputHelpTexts.annualNetIncome;
  let desiredAmountInputErrorClass = '';
  let annualIncomeInputErrorClass = '';
  let desiredAmountTextErrorClass = '';
  let annualIncomeTextErrorClass = '';
  let desiredAmountIconErrorClass = 'none';
  let annualIncomeIconErrorClass = 'none';

  if (isDesiredAmountError) {
    desiredAmountInlineText = formState.errors.desiredAmount;
    desiredAmountInputErrorClass = '!border-error-red-600';
    desiredAmountTextErrorClass = 'text-error-red-600';
    desiredAmountIconErrorClass = 'block';
  }

  if (isAnnualNetIncomeError) {
    annualIncomeInlineText = formState.errors.annualNetIncome;
    annualIncomeInputErrorClass = '!border-error-red-600';
    annualIncomeTextErrorClass = 'text-error-red-600';
    annualIncomeIconErrorClass = 'block';
  }

  return (
    <Fragment key={`${selectedAccountId}`}>
      <div className="cli-modal-wrapper">
        <div className="cli-wrapper">
          <Body>
            <div className="credit-limit-wrapper">
              <img
                src={setImagePath('credit-limit-increase')}
                className="credit-limit-image"
              />
              <div className="credit-limit-text">{requestHeading}</div>
              <div className="credit-limit-display-name">
                {displayName} *{last4Accountnumber}
              </div>
              <div className="credit-limit-amount-wrapper">
                <div className="available-amount-wrapper">
                  <div
                    className="available-credit-amount"
                    data-testid="credit-limit-available-credit"
                  >
                    {usDollarSymbol}
                    {formatAmount(availableCredit)}
                  </div>
                  <div
                    className="available-to-spend-text"
                    data-testid="credit-limit-available-spend-text"
                  >
                    {availableToSpendText}
                  </div>
                </div>
                <div className="credit-limit-divider-wrapper">
                  <span className="credit-limit-divider" />
                </div>
                <div className="cli-current-limit-wrapper">
                  <div
                    className="available-current-limit"
                    data-testid="credit-limit-current-limit"
                  >
                    {usDollarSymbol}
                    {formatAmount(creditLimit)}
                  </div>
                  <div
                    className="current-limit-text"
                    data-testid="credit-limit-current-limit-text"
                  >
                    {currentLimitText}
                  </div>
                </div>
              </div>
              <div className="as-of-date-text">
                {asOfDate} {showDateInFormat(asOfDateValue)}
              </div>
            </div>
            <div className="credit-limit-form-disclaimer-wrapper">
              <form className="credit-limit-request-form" noValidate>
                <div className="desired-amount-wrapper">
                  <input
                    id="desiredAmount"
                    type="text"
                    inputMode="numeric"
                    onInput={sanitizeInput}
                    maxLength={5}
                    name="desiredAmount"
                    required
                    className={`input-text-field syfinputfield ${desiredAmountInputErrorClass} cli-input-field`}
                    data-testid="desired-limit"
                    onChange={handleInputChange}
                  />
                  <span className="input-field-label">{desiredLimitText}</span>
                  <span className="cli-info-text">
                    <p
                      className={`validation-info-text ${desiredAmountTextErrorClass}`}
                      data-testid="desired-amount-help-text"
                    >
                      <FontAwesomeIcon
                        icon={faTriangleExclamation}
                        className="exclamation-triangle"
                        style={{
                          display: desiredAmountIconErrorClass,
                        }}
                      />
                      {desiredAmountInlineText}
                    </p>
                  </span>
                </div>
                <div className="annual-income-wrapper">
                  <input
                    id="annualNetIncome"
                    type="text"
                    inputMode="numeric"
                    onInput={sanitizeInput}
                    maxLength={8}
                    name="annualNetIncome"
                    required
                    className={`input-text-field syfinputfield ${annualIncomeInputErrorClass} cli-input-field`}
                    data-testid="annual-net-income"
                    onChange={handleInputChange}
                  />
                  <span className="input-field-label">
                    {annualNetIncomeText}
                  </span>
                  <span className="cli-info-text">
                    <p
                      className={`validation-info-text ${annualIncomeTextErrorClass}`}
                      data-testid="annual-income-help-text"
                    >
                      <FontAwesomeIcon
                        icon={faTriangleExclamation}
                        className="exclamation-triangle"
                        style={{
                          display: annualIncomeIconErrorClass,
                        }}
                      />
                      {annualIncomeInlineText}
                    </p>
                  </span>
                </div>
              </form>
              <div data-testid="disclosure-box" className="disclosure-box">
                <div className="disclosure-text">{disclosureText}</div>
              </div>
            </div>
            <div className="footer-info">{eligibilityCriteriaText}</div>
          </Body>
        </div>
      </div>
    </Fragment>
  );
};

export default CreditLimitIncreaseRequest;
